export type FormeJuridique =
  | 'SASU' | 'SAS' | 'SARL' | 'EURL'
  | 'SA' | 'SCI' | 'Auto-entrepreneur' | 'Autre';

export const FORMES_JURIDIQUES: FormeJuridique[] = [
  'SASU', 'SAS', 'SARL', 'EURL', 'SA', 'SCI', 'Auto-entrepreneur', 'Autre',
];

export interface Meta {
  tresoInitiale: number;
  societeNom: string;
  societeActivite: string;
  societeMarches: string;
  societeCreation: string; // YYYY-MM-DD
  formeJuridique: FormeJuridique;
  /** Durée du premier exercice fiscal en mois (1–34, défaut 12). */
  exercice1Duree: number;
  /** Durée du deuxième exercice fiscal en mois (1–34, défaut 12). */
  exercice2Duree: number;
  /** Durée du troisième exercice fiscal en mois (1–34, défaut 12). */
  exercice3Duree: number;
  /** Data-URL du logo société (base64). */
  logoUrl: string;
  /** Couleur primaire hex (ex. #3b82f6). */
  primaryColor: string;
  /** Taux IS normal (ex. 25). */
  tauxIS: number;
  /** Taux IS réduit PME (ex. 15). */
  tauxISReduit: number;
  /** Seuil bénéfice taux réduit PME (ex. 42500). */
  seuilISReduit: number;
  /** PFU dividendes en % (ex. 30). */
  tauxPFU: number;
  /** Taux patronal salarié par défaut en % (ex. 42). */
  defaultTauxSalarie: number;
  /** Taux charges dirigeant par défaut en % (ex. 45). */
  defaultTauxDirigeant: number;
  /** Coefficient global de scénario CA en % (100 = neutre). Multiplicateur appliqué au runtime sur tout le CA, non destructif. */
  caScenarioPct: number;
}

export interface Revenu {
  id: string;
  libelle: string;
  categorie: 'Produit' | 'Service' | 'Abonnement' | 'Licence' | 'Autre';
  mode: 'uniform' | 'monthly';
  montantUniforme: number;
  debutMois: number;
  montantsMensuels: number[];
  montantsReels: (number | null)[];  // actual figures, null = not entered
  progStart: number;
  progBase: number;
  progPct: number;
  /** Progression mois / mois en % (ex. 12 pour +12 %), une valeur par mois 0–35. */
  progPctMensuels: number[];
  /** Notes libres (contexte, hypothèses, etc.). */
  notes: string;
}

export interface Charge {
  id: string;
  libelle: string;
  mode: 'uniform' | 'monthly';
  montantUniforme: number;
  montantsMensuels: number[];
  montantsReels: (number | null)[];
  progStart: number;
  progBase: number;
  progPct: number;
  /** Taux d'auto-progression par exercice (%). Si null/absent → on utilise progPct pour toute la période. */
  progPctExercices: number[] | null;
  /** Si non null, le montant de la charge = variablePct% du revenu ciblé (calculé à la volée). */
  variableRevenuId: string | null;
  /** Si non null, le montant = variablePct% de la dépense ciblée (alternative à variableRevenuId). */
  variableChargeId: string | null;
  variablePct: number;
  /** Taux variable par exercice (%). Si null/absent → on utilise variablePct pour toute la période. */
  variablePctExercices: number[] | null;
  /** Taux variable mois par mois (%). Si non null → prioritaire sur exercices et global. */
  variablePctMensuels: number[] | null;
  /** Mois de fin (0–35). null = pas de date de fin (charge active jusqu'au bout). */
  finMois: number | null;
  notes: string;
  /** La charge est-elle fiscalement déductible ? */
  deductible: boolean;
  /** Taux de TVA applicable (ex. 20, 10, 5.5, 0). */
  tauxTVA: number;
}

export interface FreelanceCharge {
  id: string;
  nom: string;
  mission: string;
  mode: 'uniform' | 'monthly';
  montantUniforme: number;
  montantsMensuels: number[];
  montantsReels: (number | null)[];
  progStart: number;
  progBase: number;
  progPct: number;
  progPctExercices: number[] | null;
  variableRevenuId: string | null;
  variableChargeId: string | null;
  variablePct: number;
  variablePctExercices: number[] | null;
  variablePctMensuels: number[] | null;
  /** Mois de fin (0–35). null = pas de date de fin (charge active jusqu'au bout). */
  finMois: number | null;
  notes: string;
  deductible: boolean;
  tauxTVA: number;
}

export type ChargeCategorie = 'capex' | 'marketing' | 'outils' | 'tech' | 'banque' | 'legal' | 'locaux' | 'vehicules' | 'autres';

export interface Charges {
  capex: Charge[];
  marketing: Charge[];
  outils: Charge[];
  tech: Charge[];
  banque: Charge[];
  legal: Charge[];
  locaux: Charge[];
  vehicules: Charge[];
  freelances: FreelanceCharge[];
  autres: Charge[];
}

/** Catégorie métier pour regrouper les salariés (vue mensuelle, filtres). */
export interface SalaireCategorie {
  id: string;
  label: string;
}

export interface Salarie {
  id: string;
  nom: string;
  poste: string;
  /** Référence une entrée de `salaireCategories` ; null = non classé. */
  categorieId: string | null;
  moisEntree: number;
  moisSortie: number | null;
  brut: number;
  tauxPatronal: '0.42' | '0.50' | 'custom';
  customTaux: number;
  primes: number[];
  brutsReels: (number | null)[];  // actual monthly brut, null = same as planned
  notes: string;
}

export interface Dirigeant {
  id: string;
  nom: string;
  notes: string;
  /** 'brut' | 'net' | 'cout' — mode de saisie des montants de rémunération */
  modeSaisie: 'brut' | 'net' | 'cout';
  regime: string;
  customTaux: number;
  remuMode: boolean;
  remuUniform: number;
  remuMensuels: number[];
  remuReels: (number | null)[];  // actual monthly remuneration
  /** Dividendes bruts par exercice fiscal (3 ans). */
  dividendes: number[];
}

export interface AutreTaxe {
  id: string;
  label: string;
  montant: number;
  type: 'mensuel' | 'annuel';
}

export interface Investissement {
  id: string;
  libelle: string;
  /** Indice de mois (0-based) du décaissement */
  moisCible: number;
  /** Montant HT en € */
  montant: number;
  /** Fiscalement déductible (amortissable) */
  deductible: boolean;
  /** Taux TVA en % (0 | 5.5 | 10 | 20) */
  tauxTVA: number;
  notes: string;
}

export interface Fiscalite {
  tauxReduitPME: boolean;
  cfe: number[];
  autresTaxes: AutreTaxe[];
}

export interface Reel {
  ca: (number | null)[];
  charges: (number | null)[];
  masseSalariale: (number | null)[];
  treso: (number | null)[];
}

/**
 * Overrides mensuels du mode « Réel » dans la Synthèse, pour les lignes agrégées qui
 * n'ont pas d'entité sous-jacente unique (ou pour forcer une valeur réelle plutôt que
 * la somme des entités).
 *
 * Règle de précédence dans le moteur d'affichage :
 *   - si un override est défini (`!= null`) pour (key, mi) → il remplace la valeur calculée,
 *   - sinon on prend la somme des réels d'entités (ex. CA réel = Σ revenus.montantsReels),
 *   - à défaut on cascade : EBE réel = CA réel - charges réelles - MS réelle, etc.
 *
 * Chaque tableau est aligné sur le nombre total de mois de la projection.
 */
export interface ReelOverrides {
  /** Override du Total CA mensuel (€). */
  ca: (number | null)[];
  /** Override du Total Charges opérationnelles mensuel (€). */
  chargesOpex: (number | null)[];
  /** Override du Total Masse salariale mensuel (€). */
  masseSalariale: (number | null)[];
  /** Override du Total Capex / Investissements mensuel (€). */
  capex: (number | null)[];
  /** Override du coût mensuel de sous-traitance notaires (€). */
  sousTraitance: (number | null)[];
  /** Override du nombre de commandes partenaires sur le mois (purement informatif). */
  commandesPartenaires: (number | null)[];
  /** Override du coût par commande partenaire (€, purement informatif). */
  coutParCommandePartenaire: (number | null)[];
  /** Override de l'EBE mensuel (€). */
  ebe: (number | null)[];
  /** Override du Résultat avant IS mensuel (€). */
  resultatAvantIS: (number | null)[];
  /** Override du paiement d'IS du mois (€). */
  is: (number | null)[];
  /** Override de la part mensualisée de CFE (€). */
  cfe: (number | null)[];
  /** Override de la marge nette mensuelle (%). */
  margeNette: (number | null)[];
  /** Override du flux mensuel de trésorerie (€). */
  flux: (number | null)[];
  /** Override de la trésorerie cumulée à la fin du mois (€). */
  tresorerie: (number | null)[];
}

export interface UI {
  periode: '0' | '1' | '2' | 'all';
  synthesePeriode: string;
  /** Indice mois 0–35 ; null = pas de borne (début / fin de la période affichée). */
  dashboardMonthFrom: number | null;
  dashboardMonthTo: number | null;
}

export interface NotairesConfig {
  /** Si false, le calcul automatique notaires est désactivé (aucun coût injecté). */
  enabled: boolean;
  /** Panier moyen d'une commande (€). Sert à convertir le CA mensuel en nombre de commandes. */
  panierMoyen: number;
  /** Capacité d'un notaire salarié (commandes/mois). */
  capaciteParNotaire: number;
  /** Coût employeur mensuel d'un notaire salarié (€). */
  salaireNotaire: number;
  /** Nombre de notaires salariés présents au démarrage (mois 0). */
  notairesInitiaux: number;
  /** Seuil de remplissage (%) déclenchant un nouveau recrutement. */
  seuilRecrutementPct: number;
  /** Délai de recrutement : nombre de mois entre déclenchement et arrivée effective. */
  delaiRecrutement: number;
  /** Commission du notaire partenaire en % du panier moyen. Ex : 30 = 30 % reversés au partenaire par commande. */
  coutPartenairePct: number;
  /** Nombre de notaires salariés déclenchant le recrutement d'un Head of Notary Operations. */
  seuilHeadOps: number;
  /** Salaire mensuel (coût employeur) du Head of Notary Operations (€). */
  salaireHeadOps: number;
  /** 1 assistant notaire pour N notaires salariés. */
  ratioAssistant: number;
  /** Salaire mensuel (coût employeur) d'un assistant notaire (€). */
  salaireAssistant: number;
  /**
   * Override mensuel du coût réel de sous-traitance (notaires partenaires), en €.
   * Null / absent = utiliser la prévision calculée. Valeur saisie = override qui remplace
   * la prévision dans les agrégats réels (charges opex réelles, EBE réel, flux réel, trésorerie réelle).
   * Longueur alignée sur le nombre total de mois de la projection.
   */
  sousTraitanceReels?: (number | null)[];
}

export type NotaireStaffType = 'notaire' | 'head_ops' | 'assistant';

/** Ligne calculée pour un membre de l'équipe notaires (notaire, head of ops, assistant). */
export interface NotaireStaff {
  id: string;
  type: NotaireStaffType;
  nom: string;
  /** Mois (0-indexé) d'arrivée effective dans l'équipe. */
  moisArrivee: number;
  /** Coût mensuel (€). */
  coutMensuel: number;
  /** True si cette ligne vient du compte initial (M0), false si recrutée dynamiquement par l'algo. */
  initial: boolean;
}

/** Sortie de l'algorithme notaires pour un mois donné. */
export interface NotairesMonthlyOutput {
  mi: number;
  ca: number;
  commandesTotal: number;
  notairesActifs: number;
  capaciteInterne: number;
  commandesInternes: number;
  commandesPartenaires: number;
  headOpsActifs: number;
  assistantsActifs: number;
  /** Recrutements déclenchés ce mois-ci (arrivée = mi + delaiRecrutement). */
  recrutementsMois: { type: NotaireStaffType; arriveeMi: number }[];
  coutNotaires: number;
  coutPartenaires: number;
  coutHeadOps: number;
  coutAssistants: number;
  coutTotal: number;
  /** Part du CA consacrée aux coûts notaires. 0 si CA = 0. */
  pctCA: number;
}

export interface AppState {
  meta: Meta;
  revenus: Revenu[];
  charges: Charges;
  investissements: Investissement[];
  /** Catégories personnalisées pour organiser les salariés (ordre = ordre d’affichage). */
  salaireCategories: SalaireCategorie[];
  salaries: Salarie[];
  dirigeants: Dirigeant[];
  fiscalite: Fiscalite;
  reel: Reel;
  ui: UI;
  /** Budget masse salariale mensuel (coût employeur max par mois). 0 = pas de budget défini. */
  salairesBudget: number[];
  /** Mode de calcul du budget : montant fixe par mois ou pourcentage du CA. */
  salairesBudgetMode: 'fixed' | 'pct';
  /** Pourcentage du CA utilisé comme budget (mode 'pct'). Ex : 30 = 30 %. */
  salairesBudgetPct: number;
  /** Configuration du module notaires (calcul auto hybride salariés/partenaires). */
  notairesConfig: NotairesConfig;
  /**
   * Overrides mensuels saisis par l'utilisateur en mode « Réel » de la Synthèse,
   * pour les lignes agrégées qui n'ont pas d'entité éditable naturelle.
   */
  reelOverrides: ReelOverrides;
}

export interface MonthlyCalc {
  mi: number;
  ca: number;
  chargesOpex: number;
  capex: number;
  salarie: { brut: number; patronal: number; total: number };
  dirigeantCout: number;
  masseSalariale: number;
  /** Sous-traitance (notaires partenaires + autres si introduits plus tard).
   *  Incluse dans `chargesOpex`. Exposée à part pour l'affichage détaillé dans la synthèse. */
  sousTraitance: number;
  ebe: number;
  /** IS décaissé ce mois (acompte trimestriel ou solde). 0 les autres mois. */
  isPaiement: number;
  flux: number;
  tresorerie: number;
  margeNette: number;
}

export interface YearlyCalc {
  ex: number;
  ca: number;
  chargesOpex: number;
  capex: number;
  masseSalariale: number;
  ebe: number;
  resultatAvantIS: number;
  is: number;
  resultatNet: number;
  pointMort: number;
}

export interface CalcResult {
  monthly: MonthlyCalc[];
  yearly: YearlyCalc[];
  runway: number;
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface ScenarioMeta {
  id: string;
  name: string;
  description: string;
  updatedAt: string;
  isOwner: boolean;
  accessLevel: 'owner' | 'viewer' | 'editor' | 'admin';
}

export const CHARGE_CATS: { key: keyof Charges; label: string; iconName: string }[] = [
  { key: 'capex', label: 'Investissements', iconName: 'lucide:trending-up' },
  { key: 'marketing', label: 'Marketing & Pub', iconName: 'lucide:megaphone' },
  { key: 'outils', label: 'Outils & SaaS', iconName: 'lucide:wrench' },
  { key: 'tech', label: 'Technologie', iconName: 'lucide:code-2' },
  { key: 'banque', label: 'Banque & Finance', iconName: 'lucide:landmark' },
  { key: 'legal', label: 'Juridique & Légal', iconName: 'lucide:scale' },
  { key: 'locaux', label: 'Locaux & Immobilier', iconName: 'lucide:building-2' },
  { key: 'vehicules', label: 'Véhicules', iconName: 'lucide:car' },
  { key: 'freelances', label: 'Prestataires', iconName: 'lucide:user-check' },
  { key: 'autres', label: 'Autres charges', iconName: 'lucide:more-horizontal' },
];

export const REVENU_CATS: Revenu['categorie'][] = [
  'Produit', 'Service', 'Abonnement', 'Licence', 'Autre'
];
